import React, { useState, useEffect, useMemo } from 'react';
import * as R from 'ramda';
import styled from 'styled-components';
import { useSelected } from 'contexts/selected';
import { SelectedMenuProductList } from 'rangeConfig/@types';
import ProductThumbnail from 'selectedMenu/ProductThumbnail';
import AdditionalItems from 'ui/AdditionalItemsButton/AdditionalItemsButton';
import { Product } from '@inter-ikea-storageone/range';
import None from './icons/None.svg?react';
import { isMobile } from 'utils/platform';
import MobileSwiper from 'components/MobileSwiper/MobileSwiper';
import useMLSortSelected from 'hooks/useMLSortSelected';
import { getProductClusters } from 'contexts/plannerConfig';
import { useCore } from 'contexts/core';
import { sortProducts } from 'products/utils';
import { useIntl } from 'react-intl';
import KompisToolTip from 'ui/KompisToolTip/KompisToolTip';
import { useBehaviorTracker } from 'contexts/behaviors';
import { useWidget } from 'contexts/widget';

const ThumbsWrapper = styled.ul`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  align-items: center;
  min-height: 125px;
  margin: 0;
  padding-left: 0;
`;

const IconWrapper = styled.li`
  cursor: pointer;
  display: block;
  text-align: center;
`;

const StyledButton = styled.button`
  position: relative;
  background: #fff;
  cursor: pointer;
  width: 65px;
  height: 65px;
  border: none;
  text-align: center;
  box-shadow: 0 0 0 rgba(0, 0, 0, 0), 0 0 0 rgba(0, 0, 0, 0);
  &[data-selected='true'] {
    &::after {
      content: '';
      display: block;
      margin-left: auto;
      margin-right: auto;
      width: 60%;
      height: 2px;
      position: relative;
      top: 0.2rem;
      background: ${({ theme }) => theme.colors.grey[900]};
    }
  }

  @media (hover: hover) {
    &:hover {
      box-shadow: 0 2px 2px rgba(0, 0, 0, 0.1), 0 8px 8px rgba(0, 0, 0, 0.04);
    }
  }
  &:focus {
    outline: 0.125rem solid ${({ theme }) => theme.colors.feedback.focus};
  }
`;

const Image = styled.img`
  width: 100%;
`;

type Props = {
  content: SelectedMenuProductList;
  additionalItemsText?: string;
};

const ProductThumbs = ({ content, additionalItemsText }: Props) => {
  const behaviorTracker = useBehaviorTracker();
  const widgetContext = useWidget();
  const coreApi = useCore();
  const { formatMessage } = useIntl();
  const { getContentProducts, toggleAdditionalContent, getSelectedIds } = useSelected();

  const contentProducts = useMemo(() => getContentProducts(content), [content, getContentProducts]);

  const useMLSorting = !!content.useMLSorting && contentProducts.length > 1;

  const checkIfProductsChanged = (): boolean => {
    const sortByCoreProductId = R.sortBy(R.identity);
    const newIds = contentProducts.map(product => product.coreProductId);
    const prevIds = productsToShow.flatMap(products =>
      products.map(product => product.coreProductId)
    );

    return useMLSorting
      ? !R.equals(newIds, prevIds)
      : !R.equals(sortByCoreProductId(newIds), sortByCoreProductId(prevIds));
  };

  const { sortedProducts: mlSortedProducts, loading: mlLoading } = useMLSortSelected(
    contentProducts,
    useMLSorting
  );

  const loading = useMLSorting ? mlLoading : false;

  const sortedProducts =
    useMLSorting && mlSortedProducts.length
      ? mlSortedProducts
      : content.sortOrder
      ? sortProducts(contentProducts, content.sortOrder)
      : contentProducts;

  let productClusters: Product[][] = [[]];
  if (content.cluster) {
    productClusters = getProductClusters(sortedProducts)(content.cluster);
  } else {
    productClusters = sortedProducts.map(p => [p]);
  }

  if (content.sortClusteredProducts)
    productClusters = content.sortClusteredProducts(productClusters);

  const selectedIds = useMemo((): string[] => {
    return getSelectedIds(content);
  }, [content, getSelectedIds]);

  const onSelectItem = (product: Product, listIndex: number) => {
    behaviorTracker.withMeta({ listIndex }).trigger(
      'productClicked',
      {
        targets: [product]
      },
      content.behaviors
    );
  };

  const onNoneOptionSelected = () => {
    behaviorTracker.trigger('removeProducts', null, content.noneOption?.behaviors);
  };

  const sortSelectedFirst = () => {
    const selectedProductCluster = productClusters.filter(cluster =>
      cluster.find(product => selectedIds.includes(product.coreProductId))
    );
    const rest = productClusters.filter(
      cluster => !cluster.find(product => selectedIds.includes(product.coreProductId))
    );
    return [...selectedProductCluster, ...rest];
  };

  const [productsToShow, setProductsToShow] = useState<Product[][]>(sortSelectedFirst());

  useEffect(() => {
    if (checkIfProductsChanged() && !loading) {
      const sortedThumbs = sortSelectedFirst();
      setProductsToShow(sortedThumbs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedProducts]);

  if (isMobile()) {
    return (
      <MobileSwiper
        id={content.id}
        numberOfItems={10}
        selectedIds={selectedIds}
        onSelect={onSelectItem}
        selected={content.selected}
        productClusters={productsToShow}
        mainProductCardImage={content.productCardMainImage}
        onAdditionalItemsClicked={() => toggleAdditionalContent(content, widgetContext)}
        noneOption={content.noneOption}
        loading={loading}
      />
    );
  }

  const thumbnails = content.noneOption ? productsToShow.slice(0, 3) : productsToShow.slice(0, 4);
  const additionalItems = productsToShow.length - thumbnails.length;

  return (
    <ThumbsWrapper id="selectedmenu-thumbswrapper">
      {content.noneOption && (
        <IconWrapper>
          <KompisToolTip
            text={content.noneOption.title ? formatMessage({ id: content.noneOption.title }) : ''}
            uniqueId={'none-option'}
          >
            <StyledButton
              data-selected={content.noneOption.selected(coreApi)}
              onClick={() => onNoneOptionSelected()}
            >
              {content.noneOption?.image ? <Image src={content.noneOption.image} /> : <None />}
            </StyledButton>
          </KompisToolTip>
        </IconWrapper>
      )}
      {thumbnails.map(
        (products, i) =>
          products.length && (
            <ProductThumbnail
              key={products[0].coreProductId}
              product={products[0]}
              onSelect={(product: Product) => onSelectItem(product, i)}
              cluster={products}
              selectedIds={selectedIds}
              groupId={content.id}
              behaviours={content.behaviors}
              mainImage={content.productCardMainImage}
              hoverImage={content.productCardHoverImage}
              selected={content.selected}
              toolTip={content.productThumbToolTip}
              disabled={content.disabled ? content.disabled(coreApi) : false}
              listIndex={i}
            />
          )
      )}
      {additionalItems > 0 && (
        <AdditionalItems
          data-testid={`${content.id}-additional-button`}
          onClick={() => toggleAdditionalContent(content, widgetContext)}
          numberOfItems={additionalItems}
          disabled={content.disabled ? content.disabled(coreApi) : false}
          additionalItemsText={additionalItemsText}
        />
      )}
    </ThumbsWrapper>
  );
};

export default ProductThumbs;
